import { transceive } from './actions';
import { hexStringToString, byteToHexString } from './util';

// Helper function: string to hex
export function stringToHex(str) {
  return byteToHexString(new TextEncoder().encode(str));
}

// Parse OATH algorithm byte
function parseOATHAlgorithm(algoByte) {
  const algoMap = {
    '21': 'HOTP-SHA1',
    '22': 'HOTP-SHA256',
    '23': 'HOTP-SHA512',
    '11': 'TOTP-SHA1',
    '12': 'TOTP-SHA256',
    '13': 'TOTP-SHA512'
  };
  return algoMap[algoByte] || 'Unknown';
}

// Parse OATH list response
function parseOATHList(hexData) {
  const records = [];
  let offset = 0;

  while (offset < hexData.length) {
    // 71 [len] [name] [algo]
    if (hexData.substring(offset, offset + 2) === '71') {
      offset += 2;
      const len = parseInt(hexData.substring(offset, offset + 2), 16);
      offset += 2;

      const nameLen = len - 1;
      const name = hexStringToString(hexData.substring(offset, offset + nameLen * 2));
      offset += nameLen * 2;

      const algo = hexData.substring(offset, offset + 2);
      offset += 2;

      records.push({
        name: name,
        algorithm: parseOATHAlgorithm(algo),
        digits: 6,
        note: '密钥需要重新添加'
      });
    } else {
      break;
    }
  }

  return records;
}

// Export PIV certificates
export async function exportPIVCertificates(dispatch) {
  const certificates = [];
  const slots = [
    { id: '9A', tag: '5FC105', name: 'PIV Authentication' },
    { id: '9C', tag: '5FC10A', name: 'Digital Signature' },
    { id: '9D', tag: '5FC10B', name: 'Key Management' },
    { id: '9E', tag: '5FC101', name: 'Card Authentication' }
  ];

  // Select PIV applet
  await dispatch(transceive('00A4040009A00000030800001000'));

  for (const slot of slots) {
    try {
      // GET DATA command to read certificate
      const tagHex = slot.tag;
      const lc = (tagHex.length / 2).toString(16).padStart(2, '0');
      const apdu = `00CB3FFF${lc}5C03${tagHex}`;

      let response = await dispatch(transceive(apdu));
      let sw = response.slice(-4);

      if (sw === '9000') {
        const certData = response.slice(0, -4);
        if (certData && certData.length > 0) {
          certificates.push({
            slot: slot.id,
            label: slot.name,
            certificate: certData
          });
        }
      } else if (sw.startsWith('61')) {
        // Chunked reading
        let fullData = response.slice(0, -4);
        while (sw.startsWith('61')) {
          const remaining = parseInt(sw.slice(2), 16);
          const getResponse = `00C00000${remaining.toString(16).padStart(2, '0')}`;
          response = await dispatch(transceive(getResponse));
          sw = response.slice(-4);
          fullData += response.slice(0, -4);
        }
        if (fullData && fullData.length > 0) {
          certificates.push({
            slot: slot.id,
            label: slot.name,
            certificate: fullData
          });
        }
      }
    } catch (error) {
      console.error(`Failed to export certificate from slot ${slot.id}:`, error);
    }
  }

  return certificates;
}

// Export PIV cardholder info
export async function exportPIVCardholderInfo(dispatch) {
  try {
    // Select PIV applet
    await dispatch(transceive('00A4040009A00000030800001000'));

    // GET DATA - Cardholder Unique Identifier (0x5FC102)
    const apdu = '00CB3FFF055C035FC102';
    const response = await dispatch(transceive(apdu));

    if (response.slice(-4) === '9000') {
      const data = response.slice(0, -4);
      return { uuid: data };
    }
  } catch (error) {
    console.error('Failed to export PIV cardholder info:', error);
  }
  return null;
}

// Export OpenPGP personal info
export async function exportOpenPGPPersonalInfo(dispatch) {
  const info = {};

  try {
    // Select OpenPGP applet
    await dispatch(transceive('00A4040006D27600012401'));

    const tags = [
      { tag: '005E', name: 'login' },
      { tag: '5F50', name: 'url' },
      { tag: '005B', name: 'name' },
      { tag: '5F2D', name: 'language' },
      { tag: '5F35', name: 'sex' }
    ];

    for (const t of tags) {
      try {
        const apdu = `00CA${t.tag}00`;
        const response = await dispatch(transceive(apdu));

        if (response.slice(-4) === '9000') {
          const value = hexStringToString(response.slice(0, -4));
          if (value && value.length > 0) {
            info[t.name] = value;
          }
        }
      } catch (error) {
        console.error(`Failed to read ${t.name}:`, error);
      }
    }
  } catch (error) {
    console.error('Failed to export OpenPGP personal info:', error);
  }

  return info;
}

// Export OpenPGP public key fingerprints
export async function exportOpenPGPPublicKeys(dispatch) {
  const keys = [];

  try {
    // Select OpenPGP applet
    await dispatch(transceive('00A4040006D27600012401'));

    // Read key fingerprints (TAG 0xC5)
    const fingerprintAPDU = '00CA00C500';
    const fpResponse = await dispatch(transceive(fingerprintAPDU));

    if (fpResponse.slice(-4) === '9000') {
      const data = fpResponse.slice(0, -4);
      // Parse fingerprints (3 keys: signature, decryption, authentication)
      // Each fingerprint is 20 bytes (40 hex chars)
      const keyTypes = ['signature', 'decryption', 'authentication'];
      for (let i = 0; i < 3 && i * 40 < data.length; i++) {
        const fingerprint = data.substring(i * 40, (i + 1) * 40);
        if (fingerprint && !/^0+$/.test(fingerprint)) {
          keys.push({
            type: keyTypes[i],
            fingerprint: fingerprint
          });
        }
      }
    }
  } catch (error) {
    console.error('Failed to export OpenPGP public keys:', error);
  }

  return keys;
}

// Export OATH records
export async function exportOATHRecords(dispatch) {
  const records = [];

  try {
    // Select OATH applet
    await dispatch(transceive('00A4040008A000000527210101'));

    // LIST command (0xA1)
    const listAPDU = '00A1000000';
    let response = await dispatch(transceive(listAPDU));
    let sw = response.slice(-4);

    let data = response.slice(0, -4);

    // Handle chunked response
    while (sw === '61FF') {
      const continueAPDU = '00A5000000';
      response = await dispatch(transceive(continueAPDU));
      sw = response.slice(-4);
      data += response.slice(0, -4);
    }

    // Parse OATH records
    if (data && data.length > 0) {
      return parseOATHList(data);
    }
  } catch (error) {
    console.error('Failed to export OATH records:', error);
  }

  return records;
}

// Export device info
export async function exportDeviceInfo(dispatch) {
  const info = {
    serialNumber: 'Unknown',
    firmwareVersion: 'Unknown',
    model: 'Unknown'
  };

  try {
    // Select admin applet
    await dispatch(transceive('00A4040005F000000000'));

    // Try READ_ALL_INFO command (0x33)
    let res = await dispatch(transceive('0033000000'));
    if (res.endsWith('9000') && res.length > 4) {
      const data = res.substring(0, res.length - 4);
      let offset = 0;

      while (offset < data.length) {
        const tag = parseInt(data.substring(offset, offset + 2), 16);
        const length = parseInt(data.substring(offset + 2, offset + 4), 16);
        const valueHex = data.substring(offset + 4, offset + 4 + length * 2);
        offset += 4 + length * 2;

        switch (tag) {
          case 0x01: // Firmware Version
            info.firmwareVersion = hexStringToString(valueHex);
            break;
          case 0x02: // Model
            info.model = hexStringToString(valueHex);
            break;
          case 0x03: // Serial Number
            if (valueHex && !/^0+$/.test(valueHex)) {
              info.serialNumber = valueHex;
            }
            break;
        }
      }
    } else {
      // Fallback to individual reads
      res = await dispatch(transceive('0031000000'));
      if (res.endsWith('9000')) {
        info.firmwareVersion = hexStringToString(res.substring(0, res.length - 4));
      }
      res = await dispatch(transceive('0031010000'));
      if (res.endsWith('9000')) {
        info.model = hexStringToString(res.substring(0, res.length - 4));
      }
      res = await dispatch(transceive('0032000000'));
      if (res.endsWith('9000')) {
        const sn = res.substring(0, res.length - 4);
        if (sn && !/^0+$/.test(sn)) {
          info.serialNumber = sn;
        }
      }
    }
  } catch (error) {
    console.error('Failed to export device info:', error);
  }

  return info;
}

// Main export function
export async function exportBackup(dispatch, options = {}) {
  const {
    includePIV = true,
    includeOpenPGP = true,
    includeOATH = true,
    onProgress = null
  } = options;

  const backup = {
    version: '1.0',
    timestamp: new Date().toISOString(),
    device: {},
    piv: {},
    openpgp: {},
    oath: {}
  };

  let currentStep = 0;
  const totalSteps = 1 + (includePIV ? 2 : 0) + (includeOpenPGP ? 2 : 0) + (includeOATH ? 1 : 0);

  // Report progress helper
  const reportProgress = (message) => {
    currentStep++;
    if (onProgress) {
      onProgress({
        current: currentStep,
        total: totalSteps,
        message: message,
        percentage: Math.round((currentStep / totalSteps) * 100)
      });
    }
  };

  reportProgress('Reading device information...');
  backup.device = await exportDeviceInfo(dispatch);

  if (includePIV) {
    reportProgress('Exporting PIV certificates...');
    backup.piv.certificates = await exportPIVCertificates(dispatch);

    reportProgress('Exporting PIV cardholder info...');
    backup.piv.cardholderInfo = await exportPIVCardholderInfo(dispatch);
  }

  if (includeOpenPGP) {
    reportProgress('Exporting OpenPGP personal info...');
    backup.openpgp.personalInfo = await exportOpenPGPPersonalInfo(dispatch);

    reportProgress('Exporting OpenPGP public keys...');
    backup.openpgp.publicKeys = await exportOpenPGPPublicKeys(dispatch);
  }

  if (includeOATH) {
    reportProgress('Exporting OATH records...');
    backup.oath.records = await exportOATHRecords(dispatch);
  }

  return backup;
}

// Import PIV certificate
export async function importPIVCertificate(dispatch, slot, certData, pin) {
  try {
    // Select PIV applet
    await dispatch(transceive('00A4040009A00000030800001000'));

    // Verify PIN
    const pinHex = stringToHex(pin);
    const verifyAPDU = `00200080${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
    const verifyRes = await dispatch(transceive(verifyAPDU));

    if (!verifyRes.endsWith('9000')) {
      throw new Error('PIN verification failed');
    }

    // PUT DATA command to import certificate
    const slotTags = {
      '9A': '5FC105',
      '9C': '5FC10A',
      '9D': '5FC10B',
      '9E': '5FC101'
    };

    const tag = slotTags[slot];
    const certLen = (certData.length / 2).toString(16).padStart(4, '0');

    // Build TLV: 53 [len] [cert]
    const tlv = `53${certLen}${certData}`;
    const tlvLen = (tlv.length / 2).toString(16).padStart(2, '0');

    const apdu = `00DB3FFF${tlvLen}5C03${tag}${tlv}`;

    const response = await dispatch(transceive(apdu));
    return response.slice(-4) === '9000';
  } catch (error) {
    console.error(`Failed to import certificate to slot ${slot}:`, error);
    throw error;
  }
}

// Import OpenPGP personal info
export async function importOpenPGPPersonalInfo(dispatch, info, adminPin) {
  try {
    // Select OpenPGP applet
    await dispatch(transceive('00A4040006D27600012401'));

    // Verify Admin PIN (PW3)
    const pinHex = stringToHex(adminPin);
    const verifyAPDU = `00200083${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
    const verifyRes = await dispatch(transceive(verifyAPDU));

    if (!verifyRes.endsWith('9000')) {
      throw new Error('Admin PIN verification failed');
    }

    const tags = {
      'login': '005E',
      'url': '5F50',
      'name': '005B',
      'language': '5F2D',
      'sex': '5F35'
    };

    for (const [key, tag] of Object.entries(tags)) {
      if (info[key]) {
        const dataHex = stringToHex(info[key]);
        const len = (dataHex.length / 2).toString(16).padStart(2, '0');
        const apdu = `00DA${tag}${len}${dataHex}`;

        await dispatch(transceive(apdu));
      }
    }

    return true;
  } catch (error) {
    console.error('Failed to import OpenPGP personal info:', error);
    throw error;
  }
}

// Main import function
export async function importBackup(dispatch, backup, options = {}) {
  const {
    includePIV = true,
    includeOpenPGP = true,
    pivPin = '123456',
    openpgpAdminPin = '12345678',
    onProgress = null
  } = options;

  const results = {
    piv: { success: false, errors: [] },
    openpgp: { success: false, errors: [] }
  };

  let currentStep = 0;
  const pivCertCount = includePIV && backup.piv?.certificates ? backup.piv.certificates.length : 0;
  const totalSteps = pivCertCount + (includeOpenPGP ? 1 : 0);

  // Report progress helper
  const reportProgress = (message) => {
    currentStep++;
    if (onProgress) {
      onProgress({
        current: currentStep,
        total: totalSteps,
        message: message,
        percentage: Math.round((currentStep / totalSteps) * 100)
      });
    }
  };

  if (includePIV && backup.piv && backup.piv.certificates) {
    try {
      for (const cert of backup.piv.certificates) {
        try {
          reportProgress(`Importing PIV certificate: ${cert.label}...`);
          await importPIVCertificate(dispatch, cert.slot, cert.certificate, pivPin);
        } catch (error) {
          results.piv.errors.push(`Slot ${cert.slot}: ${error.message}`);
        }
      }
      results.piv.success = results.piv.errors.length === 0;
    } catch (error) {
      results.piv.errors.push(error.message);
    }
  }

  if (includeOpenPGP && backup.openpgp && backup.openpgp.personalInfo) {
    try {
      reportProgress('Importing OpenPGP personal info...');
      await importOpenPGPPersonalInfo(dispatch, backup.openpgp.personalInfo, openpgpAdminPin);
      results.openpgp.success = true;
    } catch (error) {
      results.openpgp.errors.push(error.message);
    }
  }

  return results;
}
